import { request } from "@/App/api/request";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service/analyticsService";
import { useContext } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AuthenticationContext } from "../context";
import { UserPreferences } from "../types/preferences";

export const getUserPreferences = async (authToken: string) => {
  try {
    const response = (await request<{}, UserPreferences>({
      endpoint: "/user/preferences/get",
      body: {},
    }, authToken)) as UserPreferences;
    return response;
  } catch (err) {
    logEvent(ANALYTIC_EVENTS.ERRORS.userPreferences);
    throw err;
  }
};

export const useGetUserPreferencesQuery = () => {
  const { authState, getAuthToken } = useContext(AuthenticationContext);

  const query = useQuery({
    queryFn: async () => {
      return getUserPreferences(await getAuthToken());
    },
    queryKey: ["userPreferences"],
    enabled: authState === "signedIn",
  });

  return query;
};

export const updateUserPreferences = async (authToken: string, body: Omit<UserPreferences, "created_at" | "updated_at">) => {
  return request<typeof body, UserPreferences>({
    endpoint: "/user/preferences/update",
    body: { ...body },
  }, authToken);
};

export const useUpdateUserPreferencesMutation = () => {
  const queryClient = useQueryClient();
  const { getAuthToken } = useContext(AuthenticationContext);

  const mutation = useMutation({
    mutationFn: async (body: Omit<UserPreferences, "created_at" | "updated_at">) => {
      return updateUserPreferences(await getAuthToken(), body);
    },
    mutationKey: ["userPreferences"],
    onSuccess: (data) => {
      queryClient.setQueryData(["userPreferences"], data);
    },
  });

  return mutation;
};
